import { useCallback, useEffect, useRef, useState } from 'react'
import { Show, UserButton, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import App from '@/App'
import StaffSignIn from '@/components/StaffSignIn'
import { isOwnerClaim } from '@/lib/staff-role'
import { sendStaffChat, fetchQuestions, fetchDocumentFile, type ChatTurn } from '@/lib/api'
import { staffConversation } from '@/lib/conversation'
import { saveBlob } from '@/lib/save-file'
import { fetchRenewals, renewalsLinkLabel } from '@/lib/renewals'

/**
 * THE KELVO STAFF CHAT PAGE (Kelvo build step 2, item 5).
 *
 * Signed out: the Clerk sign-in, invite-only. Signed in: the SAME chat screen
 * the public uses (src/App.tsx), sending through the Clerk-gated staff route.
 * The owner also sees a link to the questions staff could not get answered,
 * with how many are waiting.
 *
 * THIS PAGE IS NOT THE SECURITY BOUNDARY. Hiding the chat from a signed-out
 * browser is courtesy; the Worker's staff route is what refuses a stranger
 * (401), an account bound to another firm, or a role the pod does not allow
 * (403). Nothing here decides who may read what.
 *
 * A FRESH TOKEN PER QUESTION. Clerk session tokens live about a minute, and a
 * conversation on site lasts longer. getToken() returns a valid token or
 * refreshes one, so it is called for every send, never cached in state. The
 * ref keeps the transport's identity stable across renders while always
 * calling the latest getToken.
 *
 * THE CONVERSATION OUTLIVES THIS PAGE (batch 2v). It is held in memory,
 * keyed to the signed-in person (src/lib/conversation.ts), so going to
 * Questions, Documents or Facts and coming back finds it as it was. A
 * different person signing in gets an empty one; signing out, on any page,
 * discards it (src/components/StaffConversationGuard.tsx).
 */
export default function StaffChat() {
  const { isLoaded, getToken, sessionClaims, userId } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const transport = useCallback(
    async (history: ChatTurn[]) => sendStaffChat(history, await getTokenRef.current()),
    [],
  )

  // Kelvo step 3c: open the stored original behind an answer, through the
  // staff route (the Worker only opens a file that backs a live answer).
  const openDocument = useCallback(async (documentId: string, name: string) => {
    saveBlob(await fetchDocumentFile(await getTokenRef.current(), documentId, 'staff'), name)
  }, [])

  // Wait for Clerk before choosing, so a signed-in person never sees the
  // sign-in form flash on refresh.
  if (!isLoaded) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted" role="status">
          Loading…
        </p>
      </div>
    )
  }

  const owner = isOwnerClaim(sessionClaims)
  const conversation = userId ? staffConversation(userId) : undefined

  return (
    <>
      <Show when="signed-out">
        <StaffSignIn returnTo="/" />
      </Show>

      <Show when="signed-in">
        <App
          // Keyed by person as well as handed their store: if the account
          // changes while this page is open, the chat is rebuilt from nothing
          // rather than carrying anything of the last person's screen over.
          key={userId ?? 'signed-out'}
          conversation={conversation}
          transport={transport}
          openDocument={openDocument}
          // A26: the owner's links are the header's nav, not part of its
          // right-hand action, so on a phone they get a row of their own
          // instead of squeezing the name and pushing the account button off
          // the screen. Everyone else gets no nav, and the header they had.
          headerNav={
            owner ? (
              <>
                <OwnerDocumentsLink />
                <OwnerFactsLink />
                <OwnerQuestionsLink />
                <OwnerRenewalsLink />
                <OwnerReportLink />
              </>
            ) : undefined
          }
          headerAction={<UserButton />}
          showPrivacyLink={false}
        />
      </Show>
    </>
  )
}

/**
 * The owner's way in to the queue, with a count of what is waiting.
 *
 * The count is best-effort: if it cannot be loaded the link still shows, just
 * without a number. A missing number is honest; a wrong "0" would tell the
 * owner there is nothing to do when there might be.
 */
function OwnerQuestionsLink() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [waiting, setWaiting] = useState<number | null>(null)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const list = await fetchQuestions(await getTokenRef.current(), 'pending')
        if (live) setWaiting(list.length)
      } catch {
        if (live) setWaiting(null)
      }
    })()
    return () => {
      live = false
    }
  }, [])

  const label =
    waiting === null ? 'Questions' : waiting === 0 ? 'Questions' : `Questions (${waiting})`
  return (
    <Link
      to="/questions"
      className="rounded-lg border border-border-subtle px-2.5 py-1.5 text-xs font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 sm:px-3 sm:text-sm"
      aria-label={waiting ? `${waiting} questions waiting for an answer` : 'Questions from staff'}
    >
      {label}
    </Link>
  )
}

/**
 * The owner's way to what the assistant knows (Kelvo part 2c). Owner only.
 * "Facts", not "What it knows": the header holds three links beside the
 * account button on a phone, and "facts" is the word the approval screen
 * already uses ("Found 3 new facts for you to check").
 */
function OwnerFactsLink() {
  return (
    <Link
      to="/knows"
      className="rounded-lg border border-border-subtle px-2.5 py-1.5 text-xs font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 sm:px-3 sm:text-sm"
    >
      Facts
    </Link>
  )
}

/** The owner's way to his documents (Kelvo step 3c). Owner only, like Questions. */
function OwnerDocumentsLink() {
  return (
    <Link
      to="/documents"
      className="rounded-lg border border-border-subtle px-2.5 py-1.5 text-xs font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 sm:px-3 sm:text-sm"
    >
      Documents
    </Link>
  )
}

/**
 * The owner's way to his renewals (batch 2y), with a count of what is overdue
 * or due within 30 days. Owner only, like the other three. The count is
 * best-effort in the same way as Questions: a missing number is honest, a
 * wrong "0" would hide an expired certificate.
 */
function OwnerRenewalsLink() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [due, setDue] = useState<number | null>(null)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const list = await fetchRenewals(await getTokenRef.current())
        if (live) setDue(list.due)
      } catch {
        if (live) setDue(null)
      }
    })()
    return () => {
      live = false
    }
  }, [])

  return (
    <Link
      to="/renewals"
      className="rounded-lg border border-border-subtle px-2.5 py-1.5 text-xs font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 sm:px-3 sm:text-sm"
      aria-label={due ? `${due} renewals overdue or due soon` : 'Renewals'}
    >
      {renewalsLinkLabel(due)}
    </Link>
  )
}

/**
 * The owner's way to the report (Build 10c): what staff asked that was not
 * written down, and the weekly text's switch. Owner only, like the others.
 */
function OwnerReportLink() {
  return (
    <Link
      to="/report"
      className="rounded-lg border border-border-subtle px-2.5 py-1.5 text-xs font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 sm:px-3 sm:text-sm"
    >
      Report
    </Link>
  )
}
